import { useEffect, useState, type ReactNode } from "react";

type LegalSection = {
	id: string;
	label: string;
	number: string;
};

type LegalPageLayoutProps = {
	contentsLabel: string;
	description: string;
	lastUpdated: string;
	sections: LegalSection[];
	title: ReactNode;
	version: string;
	children: ReactNode;
};

export function LegalPageLayout({
	contentsLabel,
	description,
	lastUpdated,
	sections,
	title,
	version,
	children,
}: LegalPageLayoutProps) {
	const [activeSection, setActiveSection] = useState(sections[0]?.id ?? "");

	useEffect(() => {
		const sectionElements = sections
			.map((section) => document.getElementById(section.id))
			.filter((section): section is HTMLElement => Boolean(section));

		if (sectionElements.length === 0) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				const visibleEntries = entries
					.filter((entry) => entry.isIntersecting)
					.sort((a, b) => b.intersectionRatio - a.intersectionRatio);

				if (visibleEntries[0]?.target.id) {
					setActiveSection(visibleEntries[0].target.id);
				}
			},
			{
				rootMargin: "-25% 0px -55% 0px",
				threshold: [0.15, 0.35, 0.6],
			},
		);

		sectionElements.forEach((section) => observer.observe(section));

		return () => observer.disconnect();
	}, [sections]);

	return (
		<div
			className="grid grid-cols-1 gap-12 px-6 pb-24 pt-32 md:px-12 lg:grid-cols-12"
			style={{ maxWidth: "1440px", margin: "0 auto" }}
		>
			<aside className="lg:col-span-3">
				<div className="space-y-6 lg:sticky lg:top-24">
					<div>
						<div
							className="mb-4 text-xs font-bold uppercase tracking-[0.1em]"
							style={{
								fontFamily: "inherit",
								color: "#6B3FF2",
							}}
						>
							{contentsLabel}
						</div>
						<nav aria-label="Page sections" className="space-y-1">
							{sections.map((section) => {
								const isActive = activeSection === section.id;

								return (
									<a
										key={section.id}
										href={`#${section.id}`}
										aria-current={isActive ? "page" : undefined}
										className="flex items-center gap-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
										onClick={() => setActiveSection(section.id)}
										style={{
											borderLeft: isActive ? "2px solid #6B3FF2" : "2px solid transparent",
											paddingLeft: "16px",
											color: isActive ? "var(--km-on-surface)" : "var(--km-on-surface-muted)",
											fontFamily: "inherit",
										}}
									>
										<span
											style={{
												fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
												color: isActive ? "#6B3FF2" : "var(--km-on-surface-subtle, #6f6c69)",
												fontSize: "11px",
											}}
										>
											{section.number}
										</span>
										{section.label}
									</a>
								);
							})}
						</nav>
					</div>
					<div
						className="p-4"
						style={{
							background: "var(--km-surface-low)",
							boxShadow: "rgba(255,255,255,0.06) 0px 0px 0px 1px",
							borderTop: "2px solid #6B3FF2",
							borderRadius: "4px",
						}}
					>
						<div
							className="mb-1 text-xs font-bold"
							style={{
								fontFamily: "inherit",
								color: "var(--km-on-surface-muted)",
							}}
						>
							Last Updated
						</div>
						<div
							className="text-sm font-bold"
							style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "#6B3FF2" }}
						>
							{lastUpdated}
						</div>
					</div>
				</div>
			</aside>

			<main id="main-content" className="space-y-16 lg:col-span-8">
				<header className="space-y-6">
					<div className="flex items-center gap-2">
						<span
							className="inline-block h-2 w-2 rounded-full animate-pulse"
							aria-hidden="true"
							style={{ background: "#6B3FF2" }}
						/>
						<span
							className="px-3 py-1 text-xs font-medium rounded-full"
							style={{
								color: "#6B3FF2",
								boxShadow: "rgba(234,40,4,0.3) 0px 0px 0px 1px",
								background: "rgba(234,40,4,0.06)",
								letterSpacing: "0.05em",
							}}
						>
							{version}
						</span>
					</div>
					<h1
						className="font-semibold leading-none tracking-tighter"
						style={{
							fontFamily: "'Geist', -apple-system, sans-serif",
							fontSize: "clamp(3rem, 8vw, 5rem)",
							color: "var(--km-on-surface)",
						}}
					>
						{title}
					</h1>
					<p className="max-w-2xl text-base leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
						{description}
					</p>
				</header>

				{children}
			</main>
		</div>
	);
}

export function LegalSectionHeading({ number, title }: { number: string; title: string }) {
	return (
		<div className="flex items-center gap-4">
			<span
				className="shrink-0 text-xs font-bold"
				style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "var(--km-on-surface-muted)" }}
			>
				{number}
			</span>
			<div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
			<h2
				className="shrink-0 text-base font-semibold"
				style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
			>
				{title}
			</h2>
		</div>
	);
}
